import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Gift, Check, AlertTriangle, LogIn } from "lucide-react";
import { toast } from "sonner";
import { lookupReferralCode, redeemReferral } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEntitlement } from "@/context/EntitlementContext";
import { trackEvent } from "@/lib/analytics";

/**
 * /gift/:code — friend landed here from a referral link.
 * Two-state page:
 *   1. Preview → "Alice invited you · Claim 7 days free"
 *   2. After sign-in + redeem → "You got 7 days · Enjoy Cinema Mode"
 */
export default function GiftPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, showSignIn } = useAuth();
  const { refresh: refreshEntitlement } = useEntitlement();
  const [state, setState] = useState({ phase: "loading" });

  // Look up the code on mount — public endpoint, no auth needed
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await lookupReferralCode(code);
        if (alive) setState({ phase: "preview", ...data });
        trackEvent("referral_view", { code });
      } catch (err) {
        if (!alive) return;
        const detail = err?.response?.data?.detail;
        setState({
          phase: "invalid",
          message: typeof detail === "string" ? detail : (detail?.message || "This gift link is invalid or expired."),
        });
      }
    })();
    return () => { alive = false; };
  }, [code]);

  const claim = async () => {
    if (!user) {
      trackEvent("referral_signin_prompt", { code });
      showSignIn(`/gift/${code}`);
      return;
    }
    setState((s) => ({ ...s, phase: "claiming" }));
    try {
      const res = await redeemReferral(code);
      await refreshEntitlement();
      trackEvent("referral_claimed", { code });
      setState({ phase: "claimed", ...res });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Could not redeem this link.");
      toast.error(msg);
      setState((s) => ({ ...s, phase: "preview" }));
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink flex items-center justify-center px-4 py-12" data-testid="gift-page">
      <div className="w-full max-w-lg bg-paper border-2 border-ink shadow-[8px_8px_0_#1A1C20]">
        <div className="border-b-2 border-ink px-6 py-4 bg-paper-alt">
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-press-red">
            ✦ TimeCapsule 24 · A gift from a friend
          </div>
        </div>

        <div className="px-6 sm:px-8 py-8 sm:py-10">
          {state.phase === "loading" && (
            <div className="text-center py-6">
              <Loader2 size={32} className="animate-spin text-press-red mx-auto" />
              <p className="mt-4 font-mono uppercase tracking-widest text-[10px] text-ink-muted">
                Checking your gift…
              </p>
            </div>
          )}

          {state.phase === "invalid" && (
            <>
              <div className="mx-auto w-16 h-16 border-2 border-ink bg-paper-soft flex items-center justify-center mb-5">
                <AlertTriangle size={28} className="text-press-red" />
              </div>
              <h1 className="font-headline uppercase font-black text-3xl leading-[0.95] tracking-tighter">
                Link doesn&apos;t work.
              </h1>
              <p className="mt-3 font-body italic text-ink-muted">{state.message}</p>
              <button
                onClick={() => navigate("/", { replace: true })}
                className="mt-6 w-full bg-press-red text-paper border-2 border-press-red px-5 py-3 font-mono uppercase tracking-widest text-xs hover:bg-ink hover:border-ink"
                data-testid="gift-invalid-cta"
              >
                Go to TimeCapsule 24 →
              </button>
            </>
          )}

          {(state.phase === "preview" || state.phase === "claiming") && (
            <>
              <div className="mx-auto w-16 h-16 border-2 border-ink bg-paper-soft flex items-center justify-center mb-5">
                <Gift size={28} className="text-press-red" />
              </div>
              <h1 className="font-headline uppercase font-black text-3xl sm:text-4xl leading-[0.95] tracking-tighter">
                <span className="text-press-red">{state.referrer_name || "A friend"}</span>{" "}
                gifted you<br />
                {state.trial_days || 7} days of Premium.
              </h1>
              <p className="mt-4 font-body italic text-ink-muted text-lg">
                Unlimited Cinema Mode. Auto-advancing Era Binge. No credit card, no strings.
              </p>

              <ul className="mt-6 space-y-2 font-body text-sm">
                <li className="flex items-start gap-2"><Check size={16} className="mt-0.5 text-press-red" /> {state.trial_days || 7} days free — trial period only</li>
                <li className="flex items-start gap-2"><Check size={16} className="mt-0.5 text-press-red" /> No card required today</li>
                <li className="flex items-start gap-2"><Check size={16} className="mt-0.5 text-press-red" /> Cancel or upgrade anytime after</li>
              </ul>

              <button
                onClick={claim}
                disabled={state.phase === "claiming"}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-press-red text-paper border-2 border-press-red px-5 py-4 font-mono uppercase tracking-widest text-xs sm:text-sm hover:bg-ink hover:border-ink disabled:opacity-60"
                data-testid="gift-claim-btn"
              >
                {state.phase === "claiming" ? (
                  <><Loader2 size={16} className="animate-spin" /> Claiming…</>
                ) : (
                  <>{!user && <LogIn size={16} />} Claim my {state.trial_days || 7} days →</>
                )}
              </button>
              {!user && (
                <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-ink-muted text-center">
                  Sign in first so we can attach Premium to your account.
                </p>
              )}
            </>
          )}

          {state.phase === "claimed" && (
            <>
              <div className="mx-auto w-16 h-16 border-2 border-ink bg-paper-soft flex items-center justify-center mb-5">
                <Check size={28} className="text-press-red" />
              </div>
              <h1 className="font-headline uppercase font-black text-3xl sm:text-4xl leading-[0.95] tracking-tighter">
                You&apos;re Premium.
              </h1>
              <p className="mt-3 font-body italic text-ink-muted">
                {state.trial_days || 7} days on the house — enjoy Cinema Mode.
              </p>
              <button
                onClick={() => navigate("/", { replace: true })}
                className="mt-8 w-full bg-press-red text-paper border-2 border-press-red px-5 py-4 font-mono uppercase tracking-widest text-xs sm:text-sm hover:bg-ink hover:border-ink"
                data-testid="gift-continue-btn"
              >
                Start watching →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
